import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Gift } from "lucide-react";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, user } = useAuth();

  const [isLogin, setIsLogin] = useState(searchParams.get("mode") !== "register");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Pre-fill referral code from ?ref=CODE
  const prefilledRef = searchParams.get("ref") || "";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    referralCode: prefilledRef,
  });
  const [showReferralInput, setShowReferralInput] = useState(Boolean(prefilledRef));

  useEffect(() => {
    if (user) {
      navigate("/questionnaire");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        const { referralApplied } = await register(
          formData.name,
          formData.email,
          formData.password,
          formData.referralCode.trim() || undefined,
        );
        if (referralApplied) {
          toast.success("Account created — $10 referral credit added!");
        } else if (formData.referralCode) {
          toast.success("Account created (referral code not recognised)");
        } else {
          toast.success("Account created successfully!");
        }
      }
      navigate("/questionnaire");
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1635403770785-916e049592c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxyZXRpcmVkJTIwY291cGxlJTIwd2Fsa2luZyUyMG9uJTIwYmVhY2glMjBzdW5zZXR8ZW58MHx8fHwxNzcwMzEyNzAxfDA&ixlib=rb-4.1.0&q=85')"
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 p-12 flex flex-col justify-end h-full">
          <h2 className="text-4xl font-bold text-white mb-4">
            Your Next Chapter Awaits
          </h2>
          <p className="text-white/80 text-lg max-w-md">
            Join thousands who've found their perfect home abroad with personalized, 
            AI-powered recommendations.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2" data-testid="auth-title">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-muted-foreground">
              {isLogin 
                ? "Sign in to continue your expat journey" 
                : "Start your journey to finding your new home"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2 slide-in">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 bg-white/50 border-primary/20 focus:border-primary"
                  data-testid="name-input"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 bg-white/50 border-primary/20 focus:border-primary"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 bg-white/50 border-primary/20 focus:border-primary pr-12"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 btn-animated bg-primary hover:bg-primary/90 rounded-lg"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {isLogin ? "Signing in..." : "Creating account..."}
                </span>
              ) : (
                isLogin ? "Sign In" : "Create Account"
              )}
            </Button>

            {!isLogin && (
              <div className="pt-2">
                {!showReferralInput ? (
                  <button
                    type="button"
                    onClick={() => setShowReferralInput(true)}
                    className="flex items-center gap-2 text-sm text-secondary hover:underline"
                    data-testid="toggle-referral-input-btn"
                  >
                    <Gift className="h-4 w-4" />
                    Have a referral code? Get $10 off
                  </button>
                ) : (
                  <div className="space-y-2 slide-in">
                    <Label htmlFor="referralCode" className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-secondary" />
                      Referral code (optional)
                    </Label>
                    <Input
                      id="referralCode"
                      type="text"
                      placeholder="FRIEND-A7F2"
                      value={formData.referralCode}
                      onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                      className="h-11 bg-white/50 border-primary/20 focus:border-primary uppercase tracking-wider"
                      data-testid="referral-code-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      You and your friend both get $10 off when you complete your first guide.
                    </p>
                  </div>
                )}
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-secondary hover:underline font-medium"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
